
import { Type } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { XYChart, ColumnSeries, ValueAxis, CategoryAxis, AxisLabel, LabelBullet, Column } from '@amcharts/amcharts4/charts';

import { LabelDisplayOptionsEnum } from '../../../Misc/enums/label-display-options.enum';
import { ReportSeriesModel } from '../../../Reports/models/report-series.model';
import { ChartMetricsEnum } from '../../../Reports/enums/chart-metrics.enum';
import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { chartColors } from '../../enums/chart-colors.enum';
import { ChartModel } from '../chart.model';
import { DataPointModel } from '../data-point.model';
import { CategoryValueXYChartOptions } from './category-value-x-y-chart-options.model';
import { ChartSizeHelper } from '../../helpers/chart-size.helper';

export class BarChartModel extends ChartModel {
    readonly chartType: Type<XYChart> = XYChart;
    readonly singleSeries: boolean = true;
    readonly chartDisplayLimit: number = 10;
    readonly smallChartDisplayLimit: number = 3;
    declare amchart: XYChart;
    declare amseries: ColumnSeries;
    declare options: CategoryValueXYChartOptions;

    categoryAxis: CategoryAxis;
    labelTemplate: AxisLabel;
    protected columnTemplate: Column;
    protected valueAxis: ValueAxis;
    protected responsiveCategoryAxis: CategoryAxis;
    protected valueLabel: LabelBullet;

    public initChart(element: HTMLDivElement): void {
        super.initChart(element, this.chartType);

        if (this.header && this.metadata && this.metadataContainer) {
            this.metadataContainer.layout = 'vertical';
            if (this.dateRangeLabel) {
                this.dateRangeLabel.width = am4core.percent(100);
            }
            if (this.primarySeriesLabel) {
                this.primarySeriesLabel.width = am4core.percent(100);
                this.primarySeriesLabel.textAlign = 'start';
                this.primarySeriesLabel.tooltipX = 15;
            }
        } else {
            this.amchart.paddingTop = 20;
        }
        this.amchart.chartAndLegendContainer.dy = 5;

        this.valueAxis = this.amchart.xAxes.push(new ValueAxis());
        this.valueAxis.renderer.grid.template.disabled = true;
        this.valueAxis.renderer.labels.template.disabled = true;
        this.valueAxis.paddingRight = 35;
        // fixes bug with weird soft opacity vertical line to left of the bars
        this.valueAxis.renderer.baseGrid.disabled = true;

        this.categoryAxis = this.amchart.yAxes.push(new CategoryAxis());
        this.categoryAxis.dataFields.category = 'label';
        this.categoryAxis.isMeasured = false; // hack solution to make the labels appear above the bars rather than aside
        this.categoryAxis.renderer.grid.template.disabled = true;
        this.categoryAxis.renderer.minGridDistance = 1;

        // COLUMNS
        this.amseries = new ColumnSeries();

        this.amseries.dataFields.categoryX = 'percent';
        this.amseries.dataFields.valueX = 'xValue';
        this.amseries.dataFields.categoryY = 'label';
        this.amchart.series.push(this.amseries);

        // COLUMN APPEARANCE
        this.columnTemplate = this.amseries.columns.template;
        // having a stroke that is the same color as the bar itself helps fix
        // an issue where sometimes the bars will have a fraction of a pixel of width,
        // which makes nothing appear - effectively making it have a min-width
        // there is a min-width property, but it doesn't work
        this.columnTemplate.stroke = am4core.color(this.styles.colorMap?.['barColor']  || this.styles.colorMap?.['seriesColor0'] || chartColors.agprBlue);
        this.columnTemplate.strokeWidth = 1;
        this.columnTemplate.column.paddingLeft = 1; // added padding because stroke gets clipped otherwise

        this.columnTemplate.column.cornerRadiusTopLeft = 2;
        this.columnTemplate.column.cornerRadiusTopRight = 2;
        this.columnTemplate.column.cornerRadiusBottomLeft = 2;
        this.columnTemplate.column.cornerRadiusBottomRight = 2;
        this.columnTemplate.column.paddingTop = 7;
        this.columnTemplate.column.paddingBottom = 7;
        this.columnTemplate.column.paddingRight = 5;

        this.amseries.numberFormatter.smallNumberThreshold = 0;

        // set tooltip style
        this.amseries.tooltip.getFillFromObject = false;
        this.amseries.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        this.amseries.tooltip.background.stroke = am4core.color('#ced4da');
        this.amseries.tooltip.background.cornerRadius = 8;
        this.amseries.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        this.amseries.tooltip.background.filters.clear();
        this.amseries.tooltip.label.padding(0, 0, 0, 0);
        this.amseries.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        this.amseries.tooltip.keepTargetHover = true;
        // this line is actually necessary, which changed with some commit between amcharts version 4.6.1 and 4.10.18
        // otherwise, no tooltips will be shown on hover because the chart assumes no tooltip is desired, though we
        // set an adapter for it in ReportsChartComponent
        // this can also be solved by adding a cursor to the chart, but then you'd have to add more code
        // to remove all of the styling of the cursor.
        this.columnTemplate.tooltipHTML = '';
        this.amseries.columns.template.adapter.add('fill', (fill, target) => {
            return am4core.color(this.styles.colorMap?.['barColor']  || this.styles.colorMap?.['seriesColor0'] || chartColors.agprBlue);
        });

        // CATEGORY LABEL APPEARANCE
        this.labelTemplate = this.categoryAxis.renderer.labels.template;
        this.labelTemplate.align = 'left';
        this.labelTemplate.fontSize = '0.75rem';
        this.labelTemplate.dy = -20; // hack solution to make the labels appear above the bars rather than aside
        this.labelTemplate.paddingBottom = 8;
        this.labelTemplate.paddingLeft = 0;

        this.labelTemplate.tooltip = new am4core.Tooltip();
        this.labelTemplate.tooltip.getFillFromObject = false;
        this.labelTemplate.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        this.labelTemplate.tooltip.background.stroke = am4core.color('#ced4da');
        this.labelTemplate.tooltip.background.cornerRadius = 8;
        this.labelTemplate.tooltip.background.fillOpacity = 1;
        this.labelTemplate.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        this.labelTemplate.tooltip.background.filters.clear();
        this.labelTemplate.tooltip.label.padding(0, 0, 0, 0);
        this.labelTemplate.tooltip.pointerOrientation = 'left';
        this.labelTemplate.tooltip.label.interactionsEnabled = true;
        this.labelTemplate.tooltip.keepTargetHover = true;
        this.labelTemplate.tooltipHTML = '';

        // LABEL TO THE RIGHT OF THE COLUMNS
        this.valueLabel = this.amseries.bullets.push(new LabelBullet());
        this.valueLabel.events.on('ready', (event) => {
            (event.target as LabelBullet).tabindex = -1;
        });
        this.valueLabel.label.text = '';

        // Text to use as aria-label for each column
        let srText = '{label}, ';

        if (this.metadata?.enableDataDisplaySettings) {
            if (this.metadata?.labelDisplayOptions) {
                const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent);

                let symbol = '';
                if (this.options?.symbol) {
                    symbol = this.options.symbol;
                }

                if (doCount) {
                    if (
                        this.metadata.type === ReportBlockTypeEnum.TOP_10_OUTLETS
                        || this.metadata.type === ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME
                    ) {
                        this.amchart.numberFormatter.bigNumberPrefixes = [
                            { 'number': 1e+3, 'suffix': 'K' },
                            { 'number': 1e+6, 'suffix': 'M' },
                            { 'number': 1e+9, 'suffix': 'B' },
                            { 'number': 1e+12, 'suffix': 'T' },
                            { 'number': 1e+15, 'suffix': 'Q' }
                        ];
                        this.amchart.numberFormatter.smallNumberPrefixes = [];
                    }
                    this.valueLabel.label.text += `[font-style:italic]{valueX.formatNumber(\'#.#a\')}${symbol}[/]`;
                    srText += `{valueX.formatNumber(\'#.#a\')}${symbol}`;
                }

                if (doCount && doPercent) {
                    this.valueLabel.label.text += ', ';
                    srText += ', ';
                }

                if (doPercent) {
                    this.valueLabel.label.text += '[bold]{categoryX}%[/]';
                    srText += '{categoryX}%';
                }

            // this is necessary to reset the value in some situations
            } else {
                this.valueLabel.label.text = '';
                srText = '';
            }
        } else {
            const series = this.series[0] as ReportSeriesModel;
            if (series?.metric === ChartMetricsEnum.ave) {
                if (series.styles.currencySymbol){
                    this.valueLabel.label.text = `[font-style:italic]{valueX.formatNumber('${ series.styles.currencySymbol }#.#a')}[/]`;
                    srText += `{valueX.formatNumber('${ series.styles.currencySymbol }#.#a')}`;
                } else if (series.styles.currency){
                    this.valueLabel.label.text = `[font-style:italic]{valueX.formatNumber('#.#a ${ series.styles.currency }')}[/]`;
                    srText += `{valueX.formatNumber('#.#a ${ series.styles.currency }')}`;
                } else{
                    this.valueLabel.label.text = '[font-style:italic]{valueX.formatNumber(\'#.#a\')}[/]';
                    srText += '{valueX.formatNumber(\'#.#a\')}';
                }
            } else {
                this.valueLabel.label.text = '[font-style:italic]{valueX.formatNumber(\'#.#a\')}[/]';
                srText += '{valueX.formatNumber(\'#.#a\')}';
            }
        }

        this.amseries.itemReaderText = srText;

        this.valueLabel.label.fontSize = '0.75rem';
        this.valueLabel.label.horizontalCenter = 'left';
        this.valueLabel.label.hideOversized = false;
        this.valueLabel.label.truncate = false;
        // maskBullets makes the bullet labels be taken into account when sizing the bars
        this.amchart.maskBullets = false;

        // RESPONSIVE
        if (this.options.responsive) {
            // Create another category axis and use amcharts default style
            // so the labels will appear beside the bars instead of trying to style the current category axis that we have
            this.responsiveCategoryAxis = this.amchart.yAxes.push(new CategoryAxis());
            this.responsiveCategoryAxis.dataFields.category = 'label';
            this.responsiveCategoryAxis.renderer.grid.template.disabled = true;
            this.responsiveCategoryAxis.renderer.labels.template.maxWidth = 400;
            this.responsiveCategoryAxis.renderer.labels.template.truncate = true;

            this.categoryAxis.renderer.labels.template.maxWidth = 300;
            this.categoryAxis.renderer.labels.template.truncate = true;

            // Add tooltip
            this.columnTemplate.tooltip = new am4core.Tooltip();
            this.columnTemplate.tooltipText = '[bold font-size:0.75rem]{label}: [/][font-size:0.75rem]{valueX} ({categoryX}%)[/]';
            this.columnTemplate.tooltip.getFillFromObject = false;
            this.columnTemplate.tooltip.background.fill = am4core.color(chartColors.agprWhite);
            this.columnTemplate.tooltip.label.fill = am4core.color(chartColors.agprBlack);

            this.amchart.responsive.enabled = true;
            this.amchart.responsive.useDefault = false;

            // RULES

            // Laptop and desktop screen
            this.amchart.responsive.rules.push(
                {
                    id: 'large',
                    relevant: (target) => target.pixelWidth > 768,
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisRendererY) {
                            const categoryAxisState = target.labels.template.states.create(stateId);
                            categoryAxisState.properties.fontSize = 16;
                            return categoryAxisState;
                        }

                        if (target instanceof am4charts.LabelBullet) {
                            const labelBulletState = target.label.states.create(stateId);
                            labelBulletState.properties.fontSize = 16;
                            return labelBulletState;
                        }

                        if (target instanceof am4charts.ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.width = am4core.percent(100);
                            return valueAxisState;
                        }
                    }

                }
            );

            // Tablet screen
            this.amchart.responsive.rules.push(
                {
                    id: 'medium',
                    relevant: (target) => target.pixelWidth <= 700 && target.pixelWidth > 500,
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisRendererY) {
                            const categoryAxisState = target.labels.template.states.create(stateId);
                            categoryAxisState.properties.fontSize = 16;
                            return categoryAxisState;
                        }

                        if (target instanceof am4charts.LabelBullet) {
                            const labelBulletState = target.label.states.create(stateId);
                            labelBulletState.properties.fontSize = 16;
                            return labelBulletState;
                        }

                        if (target instanceof am4charts.ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.width = am4core.percent(90);
                            return valueAxisState;
                        }
                    }
                }
            );

            // Mobile screen
            this.amchart.responsive.rules.push(
                {
                    id: 'small',
                    relevant: (target) => target.pixelWidth <= 500,
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisRendererY) {
                            const categoryAxisState = target.labels.template.states.create(stateId);
                            categoryAxisState.properties.fontSize = 14;
                            return categoryAxisState;
                        }

                        if (target instanceof am4charts.LabelBullet) {
                            const labelBulletState = target.label.states.create(stateId);
                            labelBulletState.properties.fontSize = 14;
                            return labelBulletState;
                        }

                        if (target instanceof am4charts.ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.width = am4core.percent(80);
                            return valueAxisState;
                        }
                    }
                }
            );

            // This seems to handle disabled states better than responsive rules
            this.amchart.events.on('sizechanged', (event) => {
                const chart = event.target;
                if (chart.pixelWidth > 700) {
                    this.responsiveCategoryAxis.renderer.disabled = false;
                    this.categoryAxis.renderer.disabled = true;
                } else {
                    this.responsiveCategoryAxis.renderer.disabled = true;
                    this.categoryAxis.renderer.disabled = false;
                    this.labelTemplate.dy = -25;
                }
            });

            // This sets column height to a fixed size and auto-adjusts chart container height
            // so the bar won't be too big or too small when there're a few bars or many bars.
            const cellSize = 50;
            this.amchart.events.on('datavalidated', (event) => {
                const chart = event.target;
                const categoryYAxis = chart.yAxes.getIndex(0);

                // Calculate how we need to adjust chart height
                const adjustHeight = chart.data.length * cellSize - categoryYAxis.pixelHeight;
                // Get the current chart height
                const targetHeight = chart.pixelHeight + adjustHeight;
                // Set it on chart's container
                chart.svgContainer.htmlElement.style.height = targetHeight + 'px';
            });
        } else if (!this.options.isSocialStyles) {
            const smallChartHeight = ChartSizeHelper.getCellHeight();
            this.amchart.events.on('sizechanged', (event) => {
                const chart = event.target;
                const chartAreaHeight = chart.chartAndLegendContainer.pixelHeight;

                if (this.amseries.columns.values[0]) {
                    this.labelTemplate.dy = -this.amseries.columns.values[0].height / 2 - 2;
                } else {
                    // @HACK we need a better way to calculate the label position
                    if (chartAreaHeight < 300) {
                        this.labelTemplate.dy = -12;
                    } else if (chartAreaHeight < 340) {
                        this.labelTemplate.dy = -14;
                    } else if (chartAreaHeight < 360){
                        this.labelTemplate.dy = -16;
                    } else if (chartAreaHeight < 400) {
                        this.labelTemplate.dy = -18;
                    } else if (chartAreaHeight < 520) {
                        this.labelTemplate.dy = -20;
                    } else if (chartAreaHeight < 615) {
                        this.labelTemplate.dy = -22;
                    } else {
                        this.labelTemplate.dy = -30;
                    }
                }

                if (this.rawData?.length && this.rawData[0]) {
                    if (chart.pixelHeight && (chart.pixelHeight < smallChartHeight)) {
                        this.amchart.data = this.rawData[0].slice(0, this.smallChartDisplayLimit).reverse();
                    } else {
                        this.amchart.data = this.rawData[0].slice(0, this.chartDisplayLimit).reverse();
                    }
                }
            });
        }
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;

        if (!data) {
            this.amchart.data = null;
            return;
        }

        // the bar chart can only display a maximum of 10 bars. slice the first ten out.
        // the bars are placed from the bottom-up, not top-down, so the first element in the
        // dataset would appear on the bottom. this is probably less intuitive, so we reverse
        // the array to put the first element on the top of the chart.
        const smallChartHeight = ChartSizeHelper.getCellHeight();
        if (this.amchart.pixelHeight && (this.amchart.pixelHeight < smallChartHeight) && !this.isForSharedReport) {
            this.amchart.data = data[0].slice(0, this.smallChartDisplayLimit).reverse();
        } else {
            this.amchart.data = data[0].slice(0, this.chartDisplayLimit).reverse();
        }

        // when there are less than 10 items in a dataset, the horizontal bars are resized dynamically.
        // because the current solution to placing labels is to remove them from the container f1low,
        // differently sized bars make the labels appear incorrectly.
        // on load, we measure the height of the bars explicitly then offset the labels by about half that amount
        // unfortunately, it appears to be necessary to wait ~100ms before doing this.
        if (data[0] && data[0].length < 10 && data[0].length > 0 ) {
            this.amchart.events.on('validated', event => {
                // This sets column height to a fixed size and auto-adjusts chart container height
                // so the bar won't be too big when there're a few bars
                const cellSize = 40;
                const chart = event.target;
                const categoryYAxis = chart.yAxes.getIndex(0);

                // Calculate how we need to adjust chart height
                const adjustHeight = chart.data.length * cellSize - categoryYAxis.pixelHeight;
                // Get the current chart height
                const targetHeight = chart.pixelHeight + adjustHeight;
                // Set it on chart's container
                chart.svgContainer.htmlElement.style.height = targetHeight + 'px';

                setTimeout(() => {
                    if (this.amseries.columns.values[0]) {
                        this.labelTemplate.dy = -this.amseries.columns.values[0].height / 2 - 2;
                    }
                }, 100);
            });
        }
        if (!data?.includes(null) && this.isContentEmpty()) {
            this.showNoContentMessage();
            this.noContentLabel.dom.style.display = 'block';
        } else {
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
            }
        }
        this.updateSeriesStyles();
        this.ready.next(true);
    }

    updateSeriesStyles(): void {}

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);

        // Now that the height isn't dynamic, we need to scale the image so that it doesn't get stretched
        if (scale) {
            this.amchart.htmlContainer.style.width = (800 * scale) + 'px';
        }

        if (this.metadataContainer) {
            this.metadataContainer.layout = 'horizontal';
            if (this.dateRangeLabel) {
                this.dateRangeLabel.width = am4core.percent(50);
            }
            if (this.primarySeriesLabel) {
                this.primarySeriesLabel.width = am4core.percent(50);
                this.primarySeriesLabel.textAlign = 'end';
                this.primarySeriesLabel.valign = 'top';
            }

        }

        this.labelTemplate.maxWidth = 260;
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        this.labelTemplate.maxWidth = 130;

        this.amchart.seriesContainer.paddingTop = 0;

        if (this.metadataContainer) {
            this.metadataContainer.layout = 'vertical';
            if (this.dateRangeLabel) {
                this.dateRangeLabel.width = am4core.percent(100);
            }
            if (this.primarySeriesLabel) {
                this.primarySeriesLabel.textAlign = 'start';
                this.primarySeriesLabel.width = am4core.percent(100);
            }
        }

        super.unsetExportStyles();
    }

    /**
     * Exports a chart via amcharts
     */
    public async export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields: any = {
            label: this.options.label,
            xValue: this.options.xValueLabel
        };

        if (this.options.percentLabel) {
            dataFields.percent = this.options.percentLabel;
        }

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        if (type === 'csv' || type === 'json') {
            // swap out the chart's data for the rawData, which may contain more elements than the
            // maximum of |this.chartDisplayLimit| we display by default visually.
            // amcharts has a built-in adapter that should be ideal for doing exactly this task:
            // https://www.amcharts.com/docs/v4/tutorials/combining-local-data-from-multiple-series-for-export/
            // but for whatever reason, it doesn't seem to work. any value returned from the adapter was ignored
            // when I tested it. If you modify in place the 'data' parameter in the callback of the adapter,
            // that works, but then you are mutating the chart's current data instead of replacing it for export,
            // and you have to re-assign it after anyway.

            // request an animation frame because if an export takes sufficiently long, the browser may
            // decide to re-paint and the chart re-renders with the incorrect data.
            let export_;

            requestAnimationFrame(async () => {
                const temp: any[] = this.amchart.data;
                this.amchart.data = this.rawData[0];
                export_ = await this.amchart.exporting.export(type);
                this.amchart.data = temp;
            });

            return export_;
        }

        return await this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return this.amchart.data.length === 0;
    }
}
